/**
 * Shared Spread Layout Utilities
 * 
 * Common functions for calculating and rendering magazine-style page spreads.
 * Used by both admin (ImageManager) and website (ImageViewer).
 */

// Valid anchor positions (3x3 grid)
export const ANCHOR_POSITIONS = ['tl', 'tc', 'tr', 'ml', 'mc', 'mr', 'bl', 'bc', 'br'];

// Valid scale values for normal mode (contain)
export const SCALE_VALUES = [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50];

// Valid scale values for fill mode (cover) - allows zooming in
export const FILL_SCALE_VALUES = [100, 105, 110, 115, 120, 125, 130, 135, 140, 145, 150];

/** The scale `steps` steps from `current` (positive zooms in), within the values for contain or fill. */
export function stepScale(current, fill, steps) {
    const values = [...(fill ? FILL_SCALE_VALUES : SCALE_VALUES)].sort((a, b) => a - b);
    let index = values.findIndex((value) => value >= current);
    if (index < 0) index = values.length - 1;
    return values[Math.min(values.length - 1, Math.max(0, index + steps))];
}

// Default settings for images in spreads
export const DEFAULT_SPREAD_SETTINGS = {
    scale: 100,
    anchor: 'mc',
    fill: false,
};

/**
 * Map anchor position to CSS flexbox alignment styles
 * @param {string} anchor - Anchor position (tl, tc, tr, ml, mc, mr, bl, bc, br)
 * @returns {Object} Style object with alignItems and justifyContent
 */
export const getAnchorStyles = (anchor) => {
    const map = {
        tl: { alignItems: 'flex-start', justifyContent: 'flex-start' },
        tc: { alignItems: 'flex-start', justifyContent: 'center' },
        tr: { alignItems: 'flex-start', justifyContent: 'flex-end' },
        ml: { alignItems: 'center', justifyContent: 'flex-start' },
        mc: { alignItems: 'center', justifyContent: 'center' },
        mr: { alignItems: 'center', justifyContent: 'flex-end' },
        bl: { alignItems: 'flex-end', justifyContent: 'flex-start' },
        bc: { alignItems: 'flex-end', justifyContent: 'center' },
        br: { alignItems: 'flex-end', justifyContent: 'flex-end' },
    };
    return map[anchor] || map.mc;
};

/**
 * Calculate unified page dimensions from all images
 * Uses median aspect ratio to minimize white space across all images.
 * 
 * For spreads: each image occupies ONE page, so page AR = image AR
 * For single images: the image spans TWO pages, so page AR = image AR / 2
 * 
 * @param {Array} items - Array of display items
 * @param {Function} getDimensions - Function to extract dimensions from an item
 *                                   Returns { width, height } or null
 * @returns {{ width: number, height: number, aspectRatio: number }}
 */
export const calculateUnifiedPageSize = (items, getDimensions) => {
    if (!Array.isArray(items) || items.length === 0) {
        return { width: 667, height: 1000, aspectRatio: 2/3 }; // Default portrait
    }
    
    // Extract page aspect ratios based on how images are displayed
    const pageAspectRatios = [];
    
    for (const item of items) {
        if (item.type === 'spread' && item.images) {
            // Spread images: each image occupies one page
            for (const img of item.images) {
                const dims = getDimensions(img);
                if (dims?.width && dims?.height) {
                    pageAspectRatios.push(dims.width / dims.height);
                }
            }
        } else {
            // Single image spans two pages, so divide by 2 to get page aspect ratio
            const dims = getDimensions(item);
            if (dims?.width && dims?.height) {
                const imageAR = dims.width / dims.height;
                pageAspectRatios.push(imageAR / 2);
            }
        }
    }
    
    if (pageAspectRatios.length === 0) {
        return { width: 667, height: 1000, aspectRatio: 2/3 }; // Default portrait
    }
    
    // Sort and find median
    pageAspectRatios.sort((a, b) => a - b);
    const mid = Math.floor(pageAspectRatios.length / 2);
    const medianAspectRatio = pageAspectRatios.length % 2 === 0
        ? (pageAspectRatios[mid - 1] + pageAspectRatios[mid]) / 2
        : pageAspectRatios[mid];
    
    // Use a reference height and calculate width
    const refHeight = 1000;
    const refWidth = refHeight * medianAspectRatio;
    
    return { 
        width: refWidth, 
        height: refHeight, 
        aspectRatio: medianAspectRatio 
    };
};

/**
 * Page size for a spread of a set shape. The spread is two pages plus the gap,
 * so one page is spreadRatio / (2 + gap/100) wide per unit of height.
 *
 * @param {{ width, height, aspectRatio }} pageDimensions - the size the images give
 * @param {number|null} spreadRatio - spread width / height, or null to keep pageDimensions
 * @param {number} gap - gap between pages as percentage of page width
 */
export const withSpreadRatio = (pageDimensions, spreadRatio, gap = 0) => {
    if (!(spreadRatio > 0)) return pageDimensions;
    const aspectRatio = spreadRatio / (2 + (gap || 0) / 100);
    return { width: 1000 * aspectRatio, height: 1000, aspectRatio };
};

/**
 * Margins that put an absolutely positioned, inset-0 box of fixed size at an
 * anchor of its container (tl … br); the free side takes the slack.
 */
export const anchorMargins = (anchor = 'mc') => {
    const [row, col] = String(anchor || 'mc');
    const side = (start, end, key) => (key === start ? [0, 'auto'] : key === end ? ['auto', 0] : ['auto', 'auto']);
    const [marginTop, marginBottom] = side('t', 'b', row);
    const [marginLeft, marginRight] = side('l', 'r', col);
    return { marginTop, marginRight, marginBottom, marginLeft };
};

/**
 * Where the whole picture lies when an <img> box shows it with object-fit
 * contain or cover at an object-position (percentages): the box's rect plus
 * the parts cover crops off.
 */
export const pictureRect = (box, natural, fit = 'contain', position = { x: 50, y: 50 }) => {
    if (!natural?.width || !natural?.height) return box;
    const pick = fit === 'cover' ? Math.max : Math.min;
    const ratio = pick(box.width / natural.width, box.height / natural.height);
    const width = natural.width * ratio;
    const height = natural.height * ratio;
    return {
        left: box.left + (box.width - width) * (position.x / 100),
        top: box.top + (box.height - height) * (position.y / 100),
        width,
        height,
    };
};

/**
 * Width / height of a sheet of `width` × `height` px after one of its edges
 * ('left' | 'right' | 'top' | 'bottom') moved by dx / dy px. A sheet centred on
 * that axis grows on both sides, so it changes by twice the move.
 */
export const sheetRatioFromDrag = ({ width, height, edge, dx = 0, dy = 0, align = 'mc' }) => {
    const [row, col] = String(align || 'mc');
    let nextWidth = width;
    let nextHeight = height;
    if (edge === 'left' || edge === 'right') {
        nextWidth += (edge === 'left' ? -dx : dx) * (col === 'c' ? 2 : 1);
    } else {
        nextHeight += (edge === 'top' ? -dy : dy) * (row === 'm' ? 2 : 1);
    }
    return Math.max(nextWidth, 1) / Math.max(nextHeight, 1);
};

/**
 * Calculate spread layout dimensions for rendering
 * @param {Object} pageDimensions - { aspectRatio } from calculateUnifiedPageSize
 * @param {number} gap - Gap between pages as percentage of page width (0-100)
 * @param {number} maxHeight - Maximum height in pixels for the spread container
 * @param {number} maxWidth - Maximum width in pixels for the spread container (optional)
 * @returns {Object} Layout dimensions
 */
export const calculateSpreadLayout = (pageDimensions, gap = 0, maxHeight = 260, maxWidth = null) => {
    const pageAspectRatio = pageDimensions?.aspectRatio || 2/3;
    const gapFraction = gap / 100;
    
    // Calculate spread aspect ratio: 2 pages + gap
    // spreadWidth = 2 * pageWidth + gap * pageWidth = pageWidth * (2 + gap/100)
    // spreadAspectRatio = spreadWidth / pageHeight = pageAspectRatio * (2 + gap/100)
    const spreadAspectRatio = pageAspectRatio * (2 + gapFraction);
    
    // Calculate actual pixel dimensions that fit within constraints
    let spreadHeight = maxHeight;
    let spreadWidth = spreadHeight * spreadAspectRatio;
    
    // If maxWidth is specified and we exceed it, scale down
    if (maxWidth && spreadWidth > maxWidth) {
        spreadWidth = maxWidth;
        spreadHeight = spreadWidth / spreadAspectRatio;
    }
    
    // Calculate individual page dimensions
    const pageWidth = spreadHeight * pageAspectRatio;
    const pageHeight = spreadHeight;
    const gapPx = (gap / 100) * pageWidth;
    
    return {
        pageAspectRatio,
        spreadAspectRatio,
        spreadWidth,
        spreadHeight,
        pageWidth,
        pageHeight,
        gapPx,
        gapFraction,
    };
};

/**
 * Calculate page width as percentage of spread for CSS
 * @param {number} gapPercent - Gap between pages as percentage (0-100)
 * @returns {{ pageWidthPercent: number, gapWidthPercent: number }}
 */
export const calculatePagePercentages = (gapPercent = 0) => {
    // spreadWidth = 2 * pageWidth + gap * pageWidth = pageWidth * (2 + gap/100)
    // pageWidth / spreadWidth = 1 / (2 + gap/100)
    const pageWidthPercent = 100 / (2 + gapPercent / 100);
    const gapWidthPercent = (gapPercent / 100) * pageWidthPercent;
    
    return { pageWidthPercent, gapWidthPercent };
};

/**
 * Get image style for rendering within a page
 * @param {number} scale - Scale percentage (50-100 for normal, 100-150 for fill)
 * @param {boolean} fill - Whether to use cover/fill mode (full bleed)
 * @returns {Object} CSS style object
 */
export const getImageStyle = (scale = 100, fill = false) => {
    if (fill) {
        // Fill mode: image covers entire container, may crop
        // Scale > 100% zooms in (e.g., 150% = 1.5x zoom)
        const zoomScale = scale / 100;
        return {
            width: `${zoomScale * 100}%`,
            height: `${zoomScale * 100}%`,
            objectFit: 'cover',
        };
    }
    // Normal mode: image box is scale% of container, content contained within it (matches admin)
    return {
        width: `${scale}%`,
        height: `${scale}%`,
        minWidth: 0,
        minHeight: 0,
        objectFit: 'contain',
    };
};

/** Free placements snap to this step, in percent of the room the image can move in. */
export const POSITION_STEP = 10;

const ANCHOR_POINTS = {
    tl: [0, 0], tc: [50, 0], tr: [100, 0],
    ml: [0, 50], mc: [50, 50], mr: [100, 50],
    bl: [0, 100], bc: [50, 100], br: [100, 100],
};

export const isPosition = (value) => Number.isFinite(value) && value >= 0 && value <= 100;

const hasPosition = (image) => isPosition(image?.x) && isPosition(image?.y);

/**
 * Saved form of one image: the bare id when every setting is default,
 * otherwise { id } plus only the settings that differ.
 */
export function rawImageEntry(img) {
    const hasCustomScale = img.scale !== undefined && img.scale !== DEFAULT_SPREAD_SETTINGS.scale;
    const hasCustomAnchor = img.anchor !== undefined && img.anchor !== DEFAULT_SPREAD_SETTINGS.anchor;
    const hasCustomFill = img.fill === true;
    const hasCustomPosition = hasPosition(img);
    if (!hasCustomScale && !hasCustomAnchor && !hasCustomFill && !hasCustomPosition) return img.id;
    const obj = { id: img.id };
    if (hasCustomScale) obj.scale = img.scale;
    if (hasCustomAnchor) obj.anchor = img.anchor;
    if (hasCustomFill) obj.fill = true;
    if (hasCustomPosition) {
        obj.x = img.x;
        obj.y = img.y;
    }
    return obj;
}

/**
 * Apply a settings patch. A null x or y clears the free position.
 */
export function applyImageSettings(target, settings) {
    const next = {
        ...target,
        ...(settings.scale !== undefined && { scale: settings.scale }),
        ...(settings.anchor !== undefined && { anchor: settings.anchor }),
        ...(settings.fill !== undefined && { fill: settings.fill }),
    };
    if (settings.x === null || settings.y === null) {
        delete next.x;
        delete next.y;
    } else if (isPosition(settings.x) && isPosition(settings.y)) {
        next.x = settings.x;
        next.y = settings.y;
    }
    return next;
}

/**
 * gallery.images with one image's settings patched, every other entry untouched.
 *
 * @param {Array} images - gallery.images (ids, setting objects, spread pairs)
 * @param {string} imageId
 * @param {Object} settings - { scale?, anchor?, fill?, x?, y? } (null x/y clears the free position)
 * @returns {Array}
 */
export function updateRawImageSettings(images, imageId, settings) {
    const update = (entry) => {
        const id = typeof entry === 'string' ? entry : entry?.id;
        if (id !== imageId) return entry;
        return rawImageEntry(applyImageSettings(typeof entry === 'string' ? { id } : entry, settings));
    };
    return (images || []).map((item) => (Array.isArray(item) ? item.map(update) : update(item)));
}

export const snapPosition = (value, step = POSITION_STEP) => {
    const snapped = Math.round(value / step) * step;
    return Math.min(100, Math.max(0, snapped));
};

/**
 * Where an image sits in its page, as { x, y } from 0 to 100 with
 * object-position meaning: 0 puts the image's left (top) edge on the page's,
 * 100 its right (bottom) edge. A saved x/y wins over the anchor.
 * @param {{ anchor?: string, x?: number, y?: number }} image
 * @returns {{ x: number, y: number }}
 */
export const getPlacement = (image) => {
    if (isPosition(image?.x) && isPosition(image?.y)) return { x: image.x, y: image.y };
    const [x, y] = ANCHOR_POINTS[image?.anchor] || ANCHOR_POINTS.mc;
    return { x, y };
};

/** The anchor a placement lands on exactly, or null for a free position. */
export const anchorFromPlacement = ({ x, y }) => (
    Object.keys(ANCHOR_POINTS).find((key) => ANCHOR_POINTS[key][0] === x && ANCHOR_POINTS[key][1] === y) || null
);

/**
 * Settings to store for a placement. Anchor points stay anchors, so pages that
 * never use a free position keep their saved shape.
 */
export const placementSettings = ({ x, y }) => {
    const anchor = anchorFromPlacement({ x, y });
    return anchor ? { anchor, x: null, y: null } : { x, y };
};

/**
 * Style for an image placed in a page that is `position: relative; overflow: hidden`.
 * The box is scale% of the page and is offset by the same fraction of the spare
 * room that object-position uses inside it, so the image moves linearly from
 * edge to edge at any page size.
 * @param {number} scale - 50-100 (contain) or 100-150 (fill)
 * @param {boolean} fill - cover instead of contain
 * @param {{ x: number, y: number }} placement
 */
/**
 * Room the rendered picture can move in, in page pixels. Negative when the
 * picture is larger than the page (fill), which makes the same position math pan it.
 *
 * @param {{ width: number, height: number }} page - page size in pixels
 * @param {{ width: number, height: number }} natural - the picture's own size
 * @returns {{ x: number, y: number, imageWidth: number, imageHeight: number }|null}
 */
export function imageTravel(page, natural, scale = 100, fill = false) {
    if (!page?.width || !page?.height || !natural?.width || !natural?.height) return null;
    const boxW = (page.width * scale) / 100;
    const boxH = (page.height * scale) / 100;
    const k = fill
        ? Math.max(boxW / natural.width, boxH / natural.height)
        : Math.min(boxW / natural.width, boxH / natural.height);
    const imageWidth = natural.width * k;
    const imageHeight = natural.height * k;
    return { x: page.width - imageWidth, y: page.height - imageHeight, imageWidth, imageHeight };
}

const roundPosition = (value) => Math.min(100, Math.max(0, Math.round(value * 10) / 10));

/**
 * Where the picture has to sit after a zoom so the spot under `point` stays put.
 * The picture's left edge is always travel * x / 100, for contain and fill alike.
 *
 * @param {Object} args
 * @param {{ width: number, height: number }} args.page - page size in pixels
 * @param {{ width: number, height: number }} args.natural - the picture's own size
 * @param {boolean} args.fill
 * @param {{ scale: number, x: number, y: number }} args.from - current scale and placement
 * @param {number} args.toScale
 * @param {{ x: number, y: number }} args.point - focal point in page pixels
 * @returns {{ x: number, y: number }}
 */
export function zoomPlacement({ page, natural, fill, from, toScale, point }) {
    const before = imageTravel(page, natural, from.scale, fill);
    const after = imageTravel(page, natural, toScale, fill);
    if (!before || !after) return { x: from.x, y: from.y };
    const axis = (travel0, size0, travel1, size1, position, focus) => {
        if (Math.abs(travel1) < 1) return position;
        const start = (travel0 * position) / 100;
        const along = (focus - start) / size0;
        return roundPosition(((focus - along * size1) / travel1) * 100);
    };
    return {
        x: axis(before.x, before.imageWidth, after.x, after.imageWidth, from.x, point.x),
        y: axis(before.y, before.imageHeight, after.y, after.imageHeight, from.y, point.y),
    };
}

export const getPlacedImageStyle = (scale = 100, fill = false, placement = { x: 50, y: 50 }) => {
    const { x, y } = placement;
    return {
        position: 'absolute',
        left: `${((100 - scale) * x) / 100}%`,
        top: `${((100 - scale) * y) / 100}%`,
        width: `${scale}%`,
        height: `${scale}%`,
        maxWidth: 'none',
        maxHeight: 'none',
        objectFit: fill ? 'cover' : 'contain',
        objectPosition: `${x}% ${y}%`,
    };
};

/**
 * Map anchor position to CSS object-position for cover mode
 * @param {string} anchor - Anchor position (tl, tc, tr, ml, mc, mr, bl, bc, br)
 * @returns {string} CSS object-position value
 */
export const getObjectPosition = (anchor) => {
    const map = {
        tl: 'top left',
        tc: 'top center',
        tr: 'top right',
        ml: 'center left',
        mc: 'center center',
        mr: 'center right',
        bl: 'bottom left',
        bc: 'bottom center',
        br: 'bottom right',
    };
    return map[anchor] || 'center center';
};
